/**
 * Contract Hooks for interacting with deployed contracts
 * Uses wagmi hooks for write operations
 */

import { useWriteContract, useWaitForTransactionReceipt, useReadContract } from 'wagmi'
import { parseEther, parseUnits } from 'viem'
import { getContractAddress, isTestnet, TESTNET_CHAIN_IDS, MAINNET_CHAIN_IDS } from './contracts-config'

// ============================================
// Token Factory ABI
// ============================================
export const TOKEN_FACTORY_ABI = [
    {
        "inputs": [],
        "name": "creationFee",
        "outputs": [{ "internalType": "uint256", "name": "", "type": "uint256" }],
        "stateMutability": "view",
        "type": "function"
    },
    {
        "inputs": [],
        "name": "advancedFee",
        "outputs": [{ "internalType": "uint256", "name": "", "type": "uint256" }],
        "stateMutability": "view",
        "type": "function"
    },
    {
        "inputs": [
            { "internalType": "string", "name": "name_", "type": "string" },
            { "internalType": "string", "name": "symbol_", "type": "string" },
            { "internalType": "uint8", "name": "decimals_", "type": "uint8" },
            { "internalType": "uint256", "name": "totalSupply_", "type": "uint256" },
            { "internalType": "bool", "name": "burnable_", "type": "bool" },
            { "internalType": "bool", "name": "mintable_", "type": "bool" },
            { "internalType": "bool", "name": "pausable_", "type": "bool" }
        ],
        "name": "createToken",
        "outputs": [{ "internalType": "address", "name": "", "type": "address" }],
        "stateMutability": "payable",
        "type": "function"
    },
    {
        "inputs": [
            { "internalType": "string", "name": "name_", "type": "string" },
            { "internalType": "string", "name": "symbol_", "type": "string" },
            { "internalType": "uint8", "name": "decimals_", "type": "uint8" },
            { "internalType": "uint256", "name": "totalSupply_", "type": "uint256" },
            {
                "components": [
                    { "internalType": "bool", "name": "mintable", "type": "bool" },
                    { "internalType": "bool", "name": "pausable", "type": "bool" },
                    { "internalType": "uint256", "name": "buyTax", "type": "uint256" },
                    { "internalType": "uint256", "name": "sellTax", "type": "uint256" },
                    { "internalType": "uint256", "name": "transferTax", "type": "uint256" },
                    { "internalType": "address", "name": "marketingWallet", "type": "address" },
                    { "internalType": "uint256", "name": "maxTransactionAmount", "type": "uint256" },
                    { "internalType": "uint256", "name": "maxWalletAmount", "type": "uint256" },
                    { "internalType": "bool", "name": "antiWhaleEnabled", "type": "bool" },
                    { "internalType": "address", "name": "routerAddress", "type": "address" },
                    { "internalType": "bool", "name": "reflectionEnabled", "type": "bool" },
                    { "internalType": "uint256", "name": "reflectionPercent", "type": "uint256" },
                    { "internalType": "bool", "name": "autoRenounceOwnership", "type": "bool" },
                    { "internalType": "string", "name": "notes", "type": "string" }
                ],
                "internalType": "struct AdvancedToken.TokenConfig",
                "name": "config",
                "type": "tuple"
            }
        ],
        "name": "createAdvancedToken",
        "outputs": [{ "internalType": "address", "name": "", "type": "address" }],
        "stateMutability": "payable",
        "type": "function"
    },
    {
        "anonymous": false,
        "inputs": [
            { "indexed": true, "internalType": "address", "name": "token", "type": "address" },
            { "indexed": true, "internalType": "address", "name": "owner", "type": "address" },
            { "indexed": false, "internalType": "string", "name": "name", "type": "string" },
            { "indexed": false, "internalType": "string", "name": "symbol", "type": "string" },
            { "indexed": false, "internalType": "uint256", "name": "totalSupply", "type": "uint256" },
            { "indexed": false, "internalType": "bool", "name": "isAdvanced", "type": "bool" }
        ],
        "name": "TokenCreated",
        "type": "event"
    }
] as const

// ============================================
// Presale Factory ABI
// ============================================
export const PRESALE_FACTORY_ABI = [
    {
        "inputs": [],
        "name": "creationFee",
        "outputs": [{ "internalType": "uint256", "name": "", "type": "uint256" }],
        "stateMutability": "view",
        "type": "function"
    },
    {
        "inputs": [
            { "internalType": "address", "name": "token_", "type": "address" },
            { "internalType": "uint256", "name": "presaleRate_", "type": "uint256" },
            { "internalType": "uint256", "name": "softCap_", "type": "uint256" },
            { "internalType": "uint256", "name": "hardCap_", "type": "uint256" },
            { "internalType": "uint256", "name": "minContribution_", "type": "uint256" },
            { "internalType": "uint256", "name": "maxContribution_", "type": "uint256" },
            { "internalType": "uint256", "name": "startTime_", "type": "uint256" },
            { "internalType": "uint256", "name": "endTime_", "type": "uint256" },
            { "internalType": "uint256", "name": "liquidityPercent_", "type": "uint256" }
        ],
        "name": "createPresale",
        "outputs": [{ "internalType": "address", "name": "", "type": "address" }],
        "stateMutability": "payable",
        "type": "function"
    }
] as const

// ============================================
// Fair Launch Factory ABI
// ============================================
export const FAIR_LAUNCH_FACTORY_ABI = [
    {
        "inputs": [],
        "name": "creationFee",
        "outputs": [{ "internalType": "uint256", "name": "", "type": "uint256" }],
        "stateMutability": "view",
        "type": "function"
    },
    {
        "inputs": [
            { "internalType": "address", "name": "saleToken_", "type": "address" },
            { "internalType": "uint256", "name": "totalTokens_", "type": "uint256" },
            { "internalType": "uint256", "name": "softCap_", "type": "uint256" },
            { "internalType": "uint256", "name": "minContribution_", "type": "uint256" },
            { "internalType": "uint256", "name": "maxContribution_", "type": "uint256" },
            { "internalType": "uint256", "name": "startTime_", "type": "uint256" },
            { "internalType": "uint256", "name": "endTime_", "type": "uint256" },
            { "internalType": "uint256", "name": "liquidityPercent_", "type": "uint256" },
            { "internalType": "address[]", "name": "paymentTokens_", "type": "address[]" },
            { "internalType": "uint256[]", "name": "rates_", "type": "uint256[]" }
        ],
        "name": "createFairLaunch",
        "outputs": [{ "internalType": "address", "name": "", "type": "address" }],
        "stateMutability": "payable",
        "type": "function"
    },
    {
        "anonymous": false,
        "inputs": [
            { "indexed": true, "internalType": "address", "name": "presale", "type": "address" },
            { "indexed": true, "internalType": "address", "name": "token", "type": "address" },
            { "indexed": true, "internalType": "address", "name": "owner", "type": "address" },
            { "indexed": false, "internalType": "uint256", "name": "totalTokens", "type": "uint256" }
        ],
        "name": "FairLaunchCreated",
        "type": "event"
    }
] as const

// ============================================
// Token Locker ABI
// ============================================
export const TOKEN_LOCKER_ABI = [
    {
        "inputs": [],
        "name": "lockFee",
        "outputs": [{ "internalType": "uint256", "name": "", "type": "uint256" }],
        "stateMutability": "view",
        "type": "function"
    },
    {
        "inputs": [
            { "internalType": "address", "name": "token_", "type": "address" },
            { "internalType": "uint256", "name": "amount_", "type": "uint256" },
            { "internalType": "uint256", "name": "unlockTime_", "type": "uint256" },
            { "internalType": "bool", "name": "isLP_", "type": "bool" }
        ],
        "name": "lockTokens",
        "outputs": [{ "internalType": "uint256", "name": "", "type": "uint256" }],
        "stateMutability": "payable",
        "type": "function"
    },
    {
        "inputs": [
            { "internalType": "address", "name": "token_", "type": "address" },
            { "internalType": "uint256", "name": "amount_", "type": "uint256" },
            { "internalType": "uint256", "name": "startTime_", "type": "uint256" },
            { "internalType": "uint256", "name": "cliffDuration_", "type": "uint256" },
            { "internalType": "uint256", "name": "vestingDuration_", "type": "uint256" },
            { "internalType": "bool", "name": "isLP_", "type": "bool" }
        ],
        "name": "createVestingLock",
        "outputs": [{ "internalType": "uint256", "name": "", "type": "uint256" }],
        "stateMutability": "payable",
        "type": "function"
    },
    {
        "inputs": [{ "internalType": "uint256", "name": "lockId", "type": "uint256" }],
        "name": "claim",
        "outputs": [],
        "stateMutability": "nonpayable",
        "type": "function"
    },
    {
        "inputs": [{ "internalType": "uint256", "name": "lockId", "type": "uint256" }],
        "name": "getClaimableAmount",
        "outputs": [{ "internalType": "uint256", "name": "", "type": "uint256" }],
        "stateMutability": "view",
        "type": "function"
    },
    {
        "inputs": [{ "internalType": "uint256", "name": "", "type": "uint256" }],
        "name": "locks",
        "outputs": [
            { "internalType": "uint256", "name": "id", "type": "uint256" },
            { "internalType": "address", "name": "token", "type": "address" },
            { "internalType": "address", "name": "owner", "type": "address" },
            { "internalType": "uint256", "name": "amount", "type": "uint256" },
            { "internalType": "uint256", "name": "released", "type": "uint256" },
            { "internalType": "uint256", "name": "startTime", "type": "uint256" },
            { "internalType": "uint256", "name": "cliffTime", "type": "uint256" },
            { "internalType": "uint256", "name": "duration", "type": "uint256" },
            { "internalType": "uint256", "name": "unlockTime", "type": "uint256" },
            { "internalType": "bool", "name": "isLP", "type": "bool" },
            { "internalType": "bool", "name": "unlocked", "type": "bool" }
        ],
        "stateMutability": "view",
        "type": "function"
    },
    {
        "anonymous": false,
        "inputs": [
            { "indexed": true, "internalType": "uint256", "name": "lockId", "type": "uint256" },
            { "indexed": true, "internalType": "address", "name": "token", "type": "address" },
            { "indexed": true, "internalType": "address", "name": "owner", "type": "address" },
            { "indexed": false, "internalType": "uint256", "name": "amount", "type": "uint256" },
            { "indexed": false, "internalType": "uint256", "name": "unlockTime", "type": "uint256" }
        ],
        "name": "TokenLocked",
        "type": "event"
    },
    {
        "anonymous": false,
        "inputs": [
            { "indexed": true, "internalType": "uint256", "name": "lockId", "type": "uint256" },
            { "indexed": true, "internalType": "address", "name": "token", "type": "address" },
            { "indexed": true, "internalType": "address", "name": "owner", "type": "address" },
            { "indexed": false, "internalType": "uint256", "name": "amount", "type": "uint256" },
            { "indexed": false, "internalType": "uint256", "name": "startTime", "type": "uint256" },
            { "indexed": false, "internalType": "uint256", "name": "duration", "type": "uint256" }
        ],
        "name": "VestingLockCreated",
        "type": "event"
    }
] as const

// ============================================
// Get contract address for current chain
// ============================================
export function getTokenFactoryAddress(chainId: number): `0x${string}` | null {
    const address = getContractAddress(chainId, 'tokenFactory')
    return address ? (address as `0x${string}`) : null
}

export function getPresaleFactoryAddress(chainId: number): `0x${string}` | null {
    const address = getContractAddress(chainId, 'presaleFactory')
    return address ? (address as `0x${string}`) : null
}

export function getTokenLockerAddress(chainId: number): `0x${string}` | null {
    const address = getContractAddress(chainId, 'tokenLocker')
    return address ? (address as `0x${string}`) : null
}

// ============================================
// Hook: Create Token
// ============================================
export interface CreateTokenParams {
    name: string
    symbol: string
    decimals: number
    totalSupply: bigint
    burnable: boolean
    mintable: boolean
    pausable: boolean
}

export function useCreateToken(chainId: number) {
    const contractAddress = getTokenFactoryAddress(chainId)

    const { writeContract, data: hash, isPending, error } = useWriteContract()

    const { isLoading: isConfirming, isSuccess, data: receipt } = useWaitForTransactionReceipt({
        hash,
    })

    // Read creation fee
    const { data: creationFee } = useReadContract({
        address: contractAddress || undefined,
        abi: TOKEN_FACTORY_ABI,
        functionName: 'creationFee',
    })

    const createToken = async (params: CreateTokenParams) => {
        if (!contractAddress) {
            throw new Error('TokenFactory not deployed on this network')
        }

        const fee = creationFee || parseEther('0.01')

        writeContract({
            address: contractAddress,
            abi: TOKEN_FACTORY_ABI,
            functionName: 'createToken',
            args: [
                params.name,
                params.symbol,
                params.decimals,
                params.totalSupply,
                params.burnable,
                params.mintable,
                params.pausable,
            ],
            value: fee,
        })
    }

    // Extract token address from receipt logs
    const getTokenAddress = (): string | null => {
        if (!receipt?.logs) return null

        // TokenCreated(address indexed token, address indexed owner, string name, string symbol, uint256 totalSupply, bool isAdvanced)
        // keccak256("TokenCreated(address,address,string,string,uint256,bool)")
        const TOKEN_CREATED_TOPIC = '0xbf3af4428cc21d341d32afdb4ba5baf47ee5f66d5116b9018957ae6691b7ab7d'

        for (const log of receipt.logs) {
            if (log.topics && log.topics[0]?.toLowerCase() === TOKEN_CREATED_TOPIC) {
                // Token address is the first indexed parameter (topic 1)
                const topicHex = log.topics[1]
                if (topicHex && topicHex.length === 66) {
                    return '0x' + topicHex.slice(26).toLowerCase()
                }
            }
        }
        return null
    }

    return {
        createToken,
        isPending,
        isConfirming,
        isSuccess,
        error,
        hash,
        receipt,
        creationFee,
        tokenAddress: getTokenAddress(),
        contractAddress,
    }
}

// ============================================
// Hook: Create Advanced Token
// ============================================
export interface CreateAdvancedTokenParams {
    name: string
    symbol: string
    decimals: number
    totalSupply: bigint
    // Features
    mintable: boolean
    pausable: boolean
    // Tax settings (basis points, 100 = 1%)
    buyTax: number
    sellTax: number
    transferTax: number
    // Wallets
    marketingWallet: string
    routerAddress: string
    // Anti-whale
    maxTransactionAmount: bigint
    maxWalletAmount: bigint
    antiWhaleEnabled: boolean
    // Reflection
    reflectionEnabled: boolean
    reflectionPercent: number
    // Other
    autoRenounceOwnership: boolean
    notes: string
}

export function useCreateAdvancedToken(chainId: number) {
    const contractAddress = getTokenFactoryAddress(chainId)

    const { writeContract, data: hash, isPending, error } = useWriteContract()

    const { isLoading: isConfirming, isSuccess, data: receipt } = useWaitForTransactionReceipt({
        hash,
    })

    // Get fees from .env based on chain ID
    const getBaseFeeFromEnv = (): bigint => {
        let feeStr = '0.01' // default

        if (isTestnet) {
            if (chainId === TESTNET_CHAIN_IDS.BSC_TESTNET) feeStr = process.env.NEXT_PUBLIC_TOKEN_FEE_BSC || '0.01'
            else if (chainId === TESTNET_CHAIN_IDS.SEPOLIA) feeStr = process.env.NEXT_PUBLIC_TOKEN_FEE_ETH || '0.005'
            else if (chainId === TESTNET_CHAIN_IDS.POLYGON_AMOY) feeStr = process.env.NEXT_PUBLIC_TOKEN_FEE_POLYGON || '10'
            else if (chainId === TESTNET_CHAIN_IDS.BASE_SEPOLIA) feeStr = process.env.NEXT_PUBLIC_TOKEN_FEE_BASE || '0.005'
        } else {
            if (chainId === MAINNET_CHAIN_IDS.BSC) feeStr = process.env.NEXT_PUBLIC_TOKEN_FEE_BSC || '0.01'
            else if (chainId === MAINNET_CHAIN_IDS.ETH) feeStr = process.env.NEXT_PUBLIC_TOKEN_FEE_ETH || '0.005'
            else if (chainId === MAINNET_CHAIN_IDS.POLYGON) feeStr = process.env.NEXT_PUBLIC_TOKEN_FEE_POLYGON || '10'
            else if (chainId === MAINNET_CHAIN_IDS.BASE) feeStr = process.env.NEXT_PUBLIC_TOKEN_FEE_BASE || '0.005'
        }

        return parseEther(feeStr)
    }

    const getAdvancedFeeFromEnv = (): bigint => {
        let feeStr = '0.02' // default

        if (isTestnet) {
            if (chainId === TESTNET_CHAIN_IDS.BSC_TESTNET) feeStr = process.env.NEXT_PUBLIC_TOKEN_ADVANCED_FEE_BSC || '0.02'
            else if (chainId === TESTNET_CHAIN_IDS.SEPOLIA) feeStr = process.env.NEXT_PUBLIC_TOKEN_ADVANCED_FEE_ETH || '0.01'
            else if (chainId === TESTNET_CHAIN_IDS.POLYGON_AMOY) feeStr = process.env.NEXT_PUBLIC_TOKEN_ADVANCED_FEE_POLYGON || '20'
            else if (chainId === TESTNET_CHAIN_IDS.BASE_SEPOLIA) feeStr = process.env.NEXT_PUBLIC_TOKEN_ADVANCED_FEE_BASE || '0.01'
        } else {
            if (chainId === MAINNET_CHAIN_IDS.BSC) feeStr = process.env.NEXT_PUBLIC_TOKEN_ADVANCED_FEE_BSC || '0.02'
            else if (chainId === MAINNET_CHAIN_IDS.ETH) feeStr = process.env.NEXT_PUBLIC_TOKEN_ADVANCED_FEE_ETH || '0.01'
            else if (chainId === MAINNET_CHAIN_IDS.POLYGON) feeStr = process.env.NEXT_PUBLIC_TOKEN_ADVANCED_FEE_POLYGON || '20'
            else if (chainId === MAINNET_CHAIN_IDS.BASE) feeStr = process.env.NEXT_PUBLIC_TOKEN_ADVANCED_FEE_BASE || '0.01'
        }

        return parseEther(feeStr)
    }

    const creationFee = getBaseFeeFromEnv()
    const advancedFee = getAdvancedFeeFromEnv()

    // Check if advanced features are used
    const isAdvanced = (params: CreateAdvancedTokenParams) => {
        return params.buyTax > 0 || params.sellTax > 0 || params.transferTax > 0 ||
            params.antiWhaleEnabled || params.reflectionEnabled ||
            params.mintable || params.pausable
    }

    // Calculate total fee
    const getTotalFee = (params: CreateAdvancedTokenParams) => {
        return isAdvanced(params) ? creationFee + advancedFee : creationFee
    }

    const createAdvancedToken = async (params: CreateAdvancedTokenParams) => {
        if (!contractAddress) {
            throw new Error('TokenFactory not deployed on this network')
        }

        const fee = getTotalFee(params)

        // Create config struct for contract
        const config = {
            mintable: params.mintable,
            pausable: params.pausable,
            buyTax: BigInt(params.buyTax),
            sellTax: BigInt(params.sellTax),
            transferTax: BigInt(params.transferTax),
            marketingWallet: (params.marketingWallet || '0x0000000000000000000000000000000000000000') as `0x${string}`,
            maxTransactionAmount: params.maxTransactionAmount,
            maxWalletAmount: params.maxWalletAmount,
            antiWhaleEnabled: params.antiWhaleEnabled,
            routerAddress: (params.routerAddress || '0x0000000000000000000000000000000000000000') as `0x${string}`,
            reflectionEnabled: params.reflectionEnabled,
            reflectionPercent: BigInt(params.reflectionPercent),
            autoRenounceOwnership: params.autoRenounceOwnership,
            notes: params.notes,
        }

        writeContract({
            address: contractAddress,
            abi: TOKEN_FACTORY_ABI,
            functionName: 'createAdvancedToken',
            args: [
                params.name,
                params.symbol,
                params.decimals,
                params.totalSupply,
                config,
            ],
            value: fee,
        })
    }

    // Extract token address from receipt logs
    const getTokenAddress = (): string | null => {
        if (!receipt?.logs) return null

        // TokenCreated(address indexed token, address indexed owner, string name, string symbol, uint256 totalSupply, bool isAdvanced)
        const TOKEN_CREATED_TOPIC = '0xbf3af4428cc21d341d32afdb4ba5baf47ee5f66d5116b9018957ae6691b7ab7d'

        for (const log of receipt.logs) {
            if (log.topics && log.topics[0]?.toLowerCase() === TOKEN_CREATED_TOPIC) {
                const topicHex = log.topics[1]
                if (topicHex && topicHex.length === 66) {
                    return '0x' + topicHex.slice(26).toLowerCase()
                }
            }
        }
        return null
    }

    return {
        createAdvancedToken,
        isPending,
        isConfirming,
        isSuccess,
        error,
        hash,
        receipt,
        creationFee,
        advancedFee,
        tokenAddress: getTokenAddress(),
        contractAddress,
        isAdvanced,
        getTotalFee,
    }
}

// NOTE: useDeployUniqueToken hook moved to lib/deploy-unique-token.ts (client-only)

// ============================================
// Multi-Currency Presale Factory ABI
// ============================================
export const MULTI_CURRENCY_PRESALE_FACTORY_ABI = [
    {
        "inputs": [{ "internalType": "bool", "name": "featured", "type": "bool" }],
        "name": "getCreationFeeInNative",
        "outputs": [
            { "internalType": "uint256", "name": "fee", "type": "uint256" },
            { "internalType": "uint256", "name": "feeUsd", "type": "uint256" }
        ],
        "stateMutability": "view",
        "type": "function"
    },
    {
        "inputs": [
            { "internalType": "address", "name": "saleToken_", "type": "address" },
            { "internalType": "uint256", "name": "softCap_", "type": "uint256" },
            { "internalType": "uint256", "name": "hardCap_", "type": "uint256" },
            { "internalType": "uint256", "name": "minContribution_", "type": "uint256" },
            { "internalType": "uint256", "name": "maxContribution_", "type": "uint256" },
            { "internalType": "uint256", "name": "startTime_", "type": "uint256" },
            { "internalType": "uint256", "name": "endTime_", "type": "uint256" },
            { "internalType": "uint256", "name": "liquidityPercent_", "type": "uint256" },
            { "internalType": "address[]", "name": "paymentTokens_", "type": "address[]" },
            { "internalType": "uint256[]", "name": "rates_", "type": "uint256[]" },
            { "internalType": "bool", "name": "allowEarlyClaim_", "type": "bool" },
            { "internalType": "uint256", "name": "referralPercent_", "type": "uint256" },
            { "internalType": "uint256", "name": "lpLockDuration_", "type": "uint256" },
            { "internalType": "address", "name": "tokenLocker_", "type": "address" },
            { "internalType": "address", "name": "dexRouter_", "type": "address" }
        ],
        "name": "createPresale",
        "outputs": [{ "internalType": "address", "name": "", "type": "address" }],
        "stateMutability": "payable",
        "type": "function"
    },
    {
        "inputs": [
            { "internalType": "address", "name": "saleToken_", "type": "address" },
            { "internalType": "uint256", "name": "tokenAmount_", "type": "uint256" },
            { "internalType": "uint256", "name": "softCap_", "type": "uint256" },
            { "internalType": "uint256", "name": "hardCap_", "type": "uint256" },
            { "internalType": "uint256", "name": "minContribution_", "type": "uint256" },
            { "internalType": "uint256", "name": "maxContribution_", "type": "uint256" },
            { "internalType": "uint256", "name": "startTime_", "type": "uint256" },
            { "internalType": "uint256", "name": "endTime_", "type": "uint256" },
            { "internalType": "uint256", "name": "liquidityPercent_", "type": "uint256" },
            { "internalType": "address[]", "name": "paymentTokens_", "type": "address[]" },
            { "internalType": "uint256[]", "name": "rates_", "type": "uint256[]" },
            { "internalType": "bool", "name": "allowEarlyClaim_", "type": "bool" },
            { "internalType": "uint256", "name": "referralPercent_", "type": "uint256" },
            { "internalType": "uint256", "name": "lpLockDuration_", "type": "uint256" },
            { "internalType": "address", "name": "tokenLocker_", "type": "address" },
            { "internalType": "address", "name": "dexRouter_", "type": "address" }
        ],
        "name": "createPresaleWithTokens",
        "outputs": [{ "internalType": "address", "name": "", "type": "address" }],
        "stateMutability": "payable",
        "type": "function"
    },
    {
        "anonymous": false,
        "inputs": [
            { "indexed": true, "internalType": "address", "name": "presale", "type": "address" },
            { "indexed": true, "internalType": "address", "name": "token", "type": "address" },
            { "indexed": true, "internalType": "address", "name": "owner", "type": "address" },
            { "indexed": false, "internalType": "uint256", "name": "hardCap", "type": "uint256" }
        ],
        "name": "PresaleCreated",
        "type": "event"
    }
] as const

// ============================================
// Hook: Create Presale (Legacy single-currency)
// ============================================
export interface CreatePresaleParams {
    tokenAddress: string
    presaleRate: bigint
    softCap: bigint
    hardCap: bigint
    minContribution: bigint
    maxContribution: bigint
    startTime: number
    endTime: number
    liquidityPercent: number
}

export function useCreatePresale(chainId: number) {
    const contractAddress = getPresaleFactoryAddress(chainId)

    const { writeContract, data: hash, isPending, error } = useWriteContract()

    const { isLoading: isConfirming, isSuccess, data: receipt } = useWaitForTransactionReceipt({
        hash,
    })

    // Read creation fee
    const { data: creationFee } = useReadContract({
        address: contractAddress || undefined,
        abi: PRESALE_FACTORY_ABI,
        functionName: 'creationFee',
    })

    const createPresale = async (params: CreatePresaleParams) => {
        if (!contractAddress) {
            throw new Error('PresaleFactory not deployed on this network')
        }

        const fee = creationFee || parseEther('0.1')

        writeContract({
            address: contractAddress,
            abi: PRESALE_FACTORY_ABI,
            functionName: 'createPresale',
            args: [
                params.tokenAddress as `0x${string}`,
                params.presaleRate,
                params.softCap,
                params.hardCap,
                params.minContribution,
                params.maxContribution,
                BigInt(params.startTime),
                BigInt(params.endTime),
                BigInt(params.liquidityPercent),
            ],
            value: fee,
        })
    }

    return {
        createPresale,
        isPending,
        isConfirming,
        isSuccess,
        error,
        hash,
        receipt,
        creationFee,
        contractAddress,
    }
}

// ============================================
// Hook: Create Fair Launch Presale
// ============================================
export interface CreateFairLaunchParams {
    tokenAddress: string
    totalTokens: bigint
    softCap: bigint
    minContribution: bigint
    maxContribution: bigint
    startTime: number
    endTime: number
    liquidityPercent: number
    paymentTokens: string[]
    rates: bigint[]
}

export function useCreateFairLaunch(chainId: number) {
    const contractAddress = getContractAddress(chainId, 'fairLaunchFactory')

    const { writeContract, data: hash, isPending, error } = useWriteContract()

    const { isLoading: isConfirming, isSuccess, data: receipt } = useWaitForTransactionReceipt({
        hash,
    })

    const fairLaunchAddress = receipt?.logs?.[0]?.address

    const { data: creationFee } = useReadContract({
        address: (contractAddress as `0x${string}`) || undefined,
        abi: FAIR_LAUNCH_FACTORY_ABI,
        functionName: 'creationFee',
    })

    const createFairLaunch = async (params: CreateFairLaunchParams) => {
        if (!contractAddress) {
            throw new Error('FairLaunchFactory not deployed on this network')
        }

        const fee = creationFee || parseEther('0.003')

        writeContract({
            address: contractAddress as `0x${string}`,
            abi: FAIR_LAUNCH_FACTORY_ABI,
            functionName: 'createFairLaunch',
            args: [
                params.tokenAddress as `0x${string}`,
                params.totalTokens,
                params.softCap,
                params.minContribution,
                params.maxContribution,
                BigInt(params.startTime),
                BigInt(params.endTime),
                BigInt(params.liquidityPercent),
                params.paymentTokens as `0x${string}`[],
                params.rates,
            ],
            value: fee,
        })
    }

    return {
        createFairLaunch,
        isPending,
        isConfirming,
        isSuccess,
        error,
        hash,
        receipt,
        creationFee,
        contractAddress,
        presaleAddress: fairLaunchAddress,
    }
}

// ============================================
// Hook: Create Multi-Currency Presale
// ============================================
export interface PaymentToken {
    address: string  // address(0) = native BNB/ETH
    rate: bigint     // tokens per 1 unit of payment token (18 decimals)
}

export interface CreateMultiCurrencyPresaleParams {
    tokenAddress: string
    tokenAmount: bigint     // Amount of tokens to deposit to presale
    softCap: bigint
    hardCap: bigint
    minContribution: bigint
    maxContribution: bigint
    startTime: number
    endTime: number
    liquidityPercent: number
    paymentTokens: PaymentToken[]
    allowEarlyClaim: boolean
    referralPercent: bigint
    customFee?: bigint      // Optional: custom fee to pay (for premium tier)
    lpLockDuration: bigint  // LP lock duration in seconds (0 = no lock)
    tokenLocker: string     // TokenLocker contract address (address(0) = use factory default)
    dexRouter: string       // DEX router address (address(0) = use factory default)
}

// Common token addresses on BSC
export const PAYMENT_TOKENS = {
    NATIVE: '0x0000000000000000000000000000000000000000',
    // BSC Mainnet
    USDT_BSC: '0x55d398326f99059fF775485246999027B3197955',
    USDC_BSC: '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d',
    DAI_BSC: '0x1AF3F329e8BE154074D8769D1FFa4eE058B1DBc3',
    // BSC Testnet
    USDT_BSC_TESTNET: '0x337610d27c682E347C9cD60BD4b3b107C9d34dDd',
    USDC_BSC_TESTNET: '0x64544969ed7EBf5f083679233325356EbE738930',
} as const

// ERC20 ABI for approve function
export const ERC20_ABI = [
    {
        "inputs": [
            { "internalType": "address", "name": "spender", "type": "address" },
            { "internalType": "uint256", "name": "amount", "type": "uint256" }
        ],
        "name": "approve",
        "outputs": [{ "internalType": "bool", "name": "", "type": "bool" }],
        "stateMutability": "nonpayable",
        "type": "function"
    },
    {
        "inputs": [
            { "internalType": "address", "name": "owner", "type": "address" },
            { "internalType": "address", "name": "spender", "type": "address" }
        ],
        "name": "allowance",
        "outputs": [{ "internalType": "uint256", "name": "", "type": "uint256" }],
        "stateMutability": "view",
        "type": "function"
    },
    {
        "inputs": [{ "internalType": "address", "name": "account", "type": "address" }],
        "name": "balanceOf",
        "outputs": [{ "internalType": "uint256", "name": "", "type": "uint256" }],
        "stateMutability": "view",
        "type": "function"
    },
    {
        "inputs": [],
        "name": "name",
        "outputs": [{ "internalType": "string", "name": "", "type": "string" }],
        "stateMutability": "view",
        "type": "function"
    },
    {
        "inputs": [],
        "name": "symbol",
        "outputs": [{ "internalType": "string", "name": "", "type": "string" }],
        "stateMutability": "view",
        "type": "function"
    },
    {
        "inputs": [],
        "name": "decimals",
        "outputs": [{ "internalType": "uint8", "name": "", "type": "uint8" }],
        "stateMutability": "view",
        "type": "function"
    },
    {
        "inputs": [],
        "name": "totalSupply",
        "outputs": [{ "internalType": "uint256", "name": "", "type": "uint256" }],
        "stateMutability": "view",
        "type": "function"
    }
] as const

export function useCreateMultiCurrencyPresale(chainId: number) {
    // For now, use the same factory address - you'll need to deploy the new contract
    const contractAddress = getPresaleFactoryAddress(chainId)

    const { writeContract, data: hash, isPending, error, reset } = useWriteContract()

    const { isLoading: isConfirming, isSuccess, data: receipt } = useWaitForTransactionReceipt({
        hash,
    })

    // Read creation fee from contract (USD-based, converted to native via Chainlink)
    const { data: feeData } = useReadContract({
        address: contractAddress || undefined,
        abi: MULTI_CURRENCY_PRESALE_FACTORY_ABI,
        functionName: 'getCreationFeeInNative',
        args: [false], // false = not featured
    })

    // Parse fee data: [fee (native wei), feeUsd (8 decimals)]
    const creationFee = feeData ? (feeData as [bigint, bigint])[0] : undefined
    const creationFeeUsd = feeData ? Number((feeData as [bigint, bigint])[1]) / 1e8 : undefined

    // Create presale WITH token deposit (uses createPresaleWithTokens)
    // User must first approve tokens on the token contract
    const createPresale = async (params: CreateMultiCurrencyPresaleParams) => {
        if (!contractAddress) {
            throw new Error('PresaleFactory not deployed on this network')
        }

        // Use customFee if provided (for premium tier), otherwise use contract minimum fee
        const fallbackBaseFee = process.env.NEXT_PUBLIC_PRESALE_CREATION_FEE || '0';
        const fee = params.customFee || creationFee || parseEther(fallbackBaseFee)

        // Extract arrays from payment tokens
        const tokenAddresses = params.paymentTokens.map(t => t.address as `0x${string}`)
        const rates = params.paymentTokens.map(t => t.rate)

        writeContract({
            address: contractAddress,
            abi: MULTI_CURRENCY_PRESALE_FACTORY_ABI,
            functionName: 'createPresaleWithTokens',
            args: [
                params.tokenAddress as `0x${string}`,
                params.tokenAmount,
                params.softCap,
                params.hardCap,
                params.minContribution,
                params.maxContribution,
                BigInt(params.startTime),
                BigInt(params.endTime),
                BigInt(params.liquidityPercent),
                tokenAddresses,
                rates,
                params.allowEarlyClaim,
                params.referralPercent,
                params.lpLockDuration,
                params.tokenLocker as `0x${string}`,
                params.dexRouter as `0x${string}`,
            ],
            value: fee,
        })
    }

    // Approve tokens for the factory to spend
    const approveTokens = async (tokenAddress: string, amount: bigint) => {
        if (!contractAddress) {
            throw new Error('PresaleFactory not deployed on this network')
        }

        writeContract({
            address: tokenAddress as `0x${string}`,
            abi: ERC20_ABI,
            functionName: 'approve',
            args: [contractAddress, amount],
        })
    }

    // Extract presale address from receipt logs
    const getPresaleAddress = (): string | null => {
        if (!receipt?.logs) return null

        // PresaleCreated(address indexed presale, address indexed token, address indexed owner, uint256 hardCap)
        // keccak256("PresaleCreated(address,address,address,uint256)")
        const PRESALE_CREATED_TOPIC = '0xf1e139a0a87285d837232f83eb508164268b9cd45f12c0de510859d81f658731'

        for (const log of receipt.logs) {
            if (log.topics && log.topics[0]?.toLowerCase() === PRESALE_CREATED_TOPIC.toLowerCase()) {
                // Presale address is the first indexed parameter (topic 1)
                const topicHex = log.topics[1]
                if (topicHex && topicHex.length === 66) {
                    return '0x' + topicHex.slice(26).toLowerCase()
                }
            }
        }
        return null
    }

    return {
        createPresale,
        approveTokens,
        isPending,
        isConfirming,
        isSuccess,
        error,
        hash,
        receipt,
        creationFee,
        creationFeeUsd,
        contractAddress,
        presaleAddress: getPresaleAddress(),
        reset,
    }
}

// ============================================
// Hook: Token Approval
// ============================================
export function useTokenApproval(tokenAddress: string, spenderAddress: string) {
    const { writeContract, data: hash, isPending, error } = useWriteContract()
    const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({ hash })

    const approve = async (amount: bigint) => {
        if (!tokenAddress || !spenderAddress) return

        writeContract({
            address: tokenAddress as `0x${string}`,
            abi: ERC20_ABI,
            functionName: 'approve',
            args: [spenderAddress as `0x${string}`, amount],
        })
    }

    return { approve, isPending, isConfirming, isSuccess, hash, error }
}

// ============================================
// Hook: Token Locker
// ============================================
export interface LockTokensParams {
    tokenAddress: string
    amount: bigint
    unlockTime: number
    isLP: boolean
}

export interface VestingLockParams extends LockTokensParams {
    startTime: number
    cliffDuration: number
    vestingDuration: number
}

export function useTokenLocker(chainId: number) {
    const contractAddress = getTokenLockerAddress(chainId)

    const { writeContract, data: hash, isPending, error } = useWriteContract()
    const { isLoading: isConfirming, isSuccess, data: receipt } = useWaitForTransactionReceipt({ hash })

    const { data: lockFee } = useReadContract({
        address: contractAddress || undefined,
        abi: TOKEN_LOCKER_ABI,
        functionName: 'lockFee',
    })

    const lockTokens = async (params: LockTokensParams) => {
        if (!contractAddress) throw new Error('TokenLocker not deployed')

        writeContract({
            address: contractAddress,
            abi: TOKEN_LOCKER_ABI,
            functionName: 'lockTokens',
            args: [params.tokenAddress as `0x${string}`, params.amount, BigInt(params.unlockTime), params.isLP],
            value: lockFee || parseEther('0.01'),
        })
    }

    const createVestingLock = async (params: VestingLockParams) => {
        if (!contractAddress) throw new Error('TokenLocker not deployed')

        writeContract({
            address: contractAddress,
            abi: TOKEN_LOCKER_ABI,
            functionName: 'createVestingLock',
            args: [
                params.tokenAddress as `0x${string}`,
                params.amount,
                BigInt(params.startTime),
                BigInt(params.cliffDuration),
                BigInt(params.vestingDuration),
                params.isLP
            ],
            value: lockFee || parseEther('0.01'),
        })
    }

    const claim = async (lockId: bigint) => {
        if (!contractAddress) throw new Error('TokenLocker not deployed')

        writeContract({
            address: contractAddress,
            abi: TOKEN_LOCKER_ABI,
            functionName: 'claim',
            args: [lockId],
        })
    }

    // Get lock ID from logs
    const getLockIdFromReceipt = (): bigint | null => {
        if (!receipt?.logs) return null

        for (const log of receipt.logs) {
            if (log.address.toLowerCase() === contractAddress?.toLowerCase()) {
                if (log.topics && log.topics.length > 1) {
                    const topic = log.topics[1];
                    if (topic) return BigInt(topic);
                }
            }
        }
        return null
    }

    return {
        lockTokens,
        createVestingLock,
        claim,
        isPending,
        isConfirming,
        isSuccess,
        error,
        hash,
        receipt,
        lockFee,
        contractId: getLockIdFromReceipt(),
        contractAddress,
    }
}
